import React, { useState, useEffect, useRef } from 'react';
import './PromptDemoUI.css';

// ── Battle data ──────────────────────────────────────────────────────────────
const BATTLES = [
  {
    id: 847,
    division: 'Gold',
    divIcon: '🥇',
    format: '2 MIN BLITZ',
    totalSeconds: 120,
    challenge: 'Write a prompt that makes AI draft a follow-up email for an unpaid invoice — professional, not pushy, with a soft deadline.',
    situation: 'Invoice #1047 · $2,400 · sent Oct 3rd · no reply, no payment',
    you: {
      name: 'You',
      rank: '#312',
      prompt: `Act as a professional account manager.\nWrite a follow-up for Invoice #1047 ($2,400).\nTone: confident, not apologetic.\nInclude: invoice #, amount, original date.\nEnd with a soft 7-day deadline CTA.`,
      score: 91,
      breakdown: { accuracy: 38, efficiency: 22, creativity: 18, alignment: 13 },
    },
    opponent: {
      name: 'rival_dev',
      rank: '#289',
      prompt: `Write a short email reminding a client about\nan unpaid invoice. Keep it polite.\nMention the invoice number.\nAsk them to pay soon.`,
      score: 64,
      breakdown: { accuracy: 26, efficiency: 18, creativity: 10, alignment: 10 },
    },
  },
  {
    id: 901,
    division: 'Platinum',
    divIcon: '💎',
    format: '5 MIN SPRINT',
    totalSeconds: 300,
    challenge: 'Prompt an AI to explain a complex machine learning concept to a 12-year-old using only a football analogy.',
    situation: 'Concept: Gradient Descent · Audience: age 12 · Max length: 150 words',
    you: {
      name: 'You',
      rank: '#198',
      prompt: `You are a football coach explaining tactics.\nExplain gradient descent using only football:\n- players = weights\n- winning = minimising loss\n- coach adjustments = gradient steps\nMax 120 words. Age 12 audience.\nEnd with one memorable analogy sentence.`,
      score: 88,
      breakdown: { accuracy: 35, efficiency: 23, creativity: 19, alignment: 11 },
    },
    opponent: {
      name: 'ml_maestro',
      rank: '#145',
      prompt: `Explain gradient descent to a kid using\na football game as an example.\nKeep it simple and fun.`,
      score: 71,
      breakdown: { accuracy: 29, efficiency: 20, creativity: 14, alignment: 8 },
    },
  },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function fmtTime(s) { return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`; }

// ── Sub-components ────────────────────────────────────────────────────────────
function ScoreRing({ score, won, animate }) {
  const r = 26;
  const circ = 2 * Math.PI * r;
  const offset = circ - (circ * (animate ? score : 0)) / 100;
  return (
    <div className={`pd2-ring ${won ? 'pd2-ring--won' : 'pd2-ring--lost'}`}>
      <svg viewBox="0 0 64 64" width="64" height="64">
        <circle className="pd2-ring-bg" cx="32" cy="32" r={r} />
        <circle
          className="pd2-ring-fill"
          cx="32" cy="32" r={r}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: animate ? 'stroke-dashoffset 1.4s cubic-bezier(0.4,0,0.2,1) 0.3s' : 'none' }}
        />
      </svg>
      <span className="pd2-ring-num">{animate ? score : 0}</span>
    </div>
  );
}

function BreakdownBar({ label, val, max, delay }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(val), delay);
    return () => clearTimeout(t);
  }, [val, delay]);
  return (
    <div className="pd2-bar-row">
      <span className="pd2-bar-label">{label}</span>
      <div className="pd2-bar-track">
        <div className="pd2-bar-fill" style={{ width: `${(w / max) * 100}%` }} />
      </div>
      <span className="pd2-bar-val">{val}%</span>
    </div>
  );
}

// ── Swords VS Divider ─────────────────────────────────────────────────────────
function SwordsDivider() {
  return (
    <div className="pd2-vs">
      <div className="pd2-vs-line" />
      <div className="pd2-swords-divider">
        <span className="pd2-clash-emoji">⚔️</span>
        <span className="pd2-vs-label">VS</span>
      </div>
      <div className="pd2-vs-line" />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PromptDemoUI({ user, openAuthModal, navigate }) {
  const [battleIdx, setBattleIdx] = useState(0);
  const [phase, setPhase] = useState('countdown'); // countdown | typing | reveal | done
  const [timeLeft, setTimeLeft] = useState(0);
  const [youTyped, setYouTyped] = useState(0);
  const [oppTyped, setOppTyped] = useState(0);
  const [showScores, setShowScores] = useState(false);
  const timerRef = useRef(null);
  const battle = BATTLES[battleIdx];

  // ── Auth-gated nav handlers (same pattern as LandingPage) ─────────────────
  const handleEnterArena = () => {
    if (user) {
      navigate && navigate('/tasks');
    } else {
      openAuthModal && openAuthModal('signup');
    }
  };

  const handleLeaderboard = () => {
    if (user) {
      navigate && navigate('/leaderboard');
    } else {
      openAuthModal && openAuthModal('signup');
    }
  };

  // ── Reset & run battle ─────────────────────────────────────────────────────
  const runBattle = (idx) => {
    const b = BATTLES[idx];
    setPhase('countdown');
    setTimeLeft(b.totalSeconds);
    setYouTyped(0);
    setOppTyped(0);
    setShowScores(false);

    setTimeout(() => {
      setPhase('typing');
      const youLen = b.you.prompt.length;
      const oppLen = b.opponent.prompt.length;
      const duration = Math.min(b.totalSeconds * 0.55, 18) * 1000;
      const interval = 60;
      const youStep = youLen / (duration / interval);
      const oppStep = oppLen / (duration / interval);
      let youCur = 0, oppCur = 0;

      const typeTimer = setInterval(() => {
        youCur = Math.min(youCur + youStep, youLen);
        oppCur = Math.min(oppCur + oppStep, oppLen);
        setYouTyped(Math.round(youCur));
        setOppTyped(Math.round(oppCur));
        if (youCur >= youLen && oppCur >= oppLen) {
          clearInterval(typeTimer);
          setTimeout(() => {
            setPhase('reveal');
            setShowScores(true);
            setTimeout(() => setPhase('done'), 3000);
          }, 800);
        }
      }, interval);
    }, 1200);
  };

  useEffect(() => {
    if (phase === 'typing' || phase === 'reveal') {
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => Math.max(0, t - 1));
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  useEffect(() => {
    if (phase === 'done') {
      const t = setTimeout(() => {
        const next = (battleIdx + 1) % BATTLES.length;
        setBattleIdx(next);
        runBattle(next);
      }, 3500);
      return () => clearTimeout(t);
    }
  }, [phase]);

  useEffect(() => { runBattle(0); }, []);

  const youPromptDisplay = battle.you.prompt.slice(0, youTyped);
  const oppPromptDisplay = battle.opponent.prompt.slice(0, oppTyped);
  const timerDanger = timeLeft <= 20 && phase === 'typing';
  const youWon = battle.you.score > battle.opponent.score;

  return (
    <section className="pd2-hero" aria-label="Head-to-Head Battle Arena">
      <div className="pd2-grid-bg" />
      <div className="pd2-orb pd2-orb-1" />
      <div className="pd2-orb pd2-orb-2" />
      <div className="pd2-orb pd2-orb-3" />

      <div className="pd2-container">

        {/* ── LEFT copy ── */}
        <div className="pd2-left">
         <h1 className="pd2-title">
  May the best<br />
  <span className="pd2-title-accent">prompt survive.</span>
</h1>

          <p className="pd2-subtitle">
             <span className="pd2-subtitle-em">The most interactive way to learn prompt engineering. </span>
            Compete head-to-head in ranked battles, challenge a friend, or practice solo.— climb the ELO leaderboard one prompt at a time.
            <br />
          </p>

          <div className="pd2-stat-row">
            {[
              { n: '1v1', l: 'Challenge Play' },
              { n: '5000+', l: 'Interactive Challenges' },
              { n: '2m / 5m / 10m', l: 'Timed formats' },
            ].map((s) => (
              <div key={s.l} className="pd2-stat">
                <span className="pd2-stat-n">{s.n}</span>
                <span className="pd2-stat-l">{s.l}</span>
              </div>
            ))}
          </div>

          <div className="pd2-buttons">
            <button
              className="pd2-btn-primary"
              onClick={handleEnterArena}
            >
              Enter the Field
            </button>
            <button
              className="pd2-btn-ghost"
              onClick={handleLeaderboard}
            >
              🌍 World Leaderboard
            </button>
          </div>
        </div>

        {/* ── RIGHT: battle card ── */}
        <div className="pd2-battle-card">

          {/* card header */}
          <div className="pd2-card-head">
            <div className="pd2-card-head-left">
              <span className="pd2-live-badge">
                <span className="pd2-live-pulse" />
                LIVE
              </span>
              <span className="pd2-match-id">Match #{battle.id}</span>
              <span className="pd2-format-pill">{battle.format}</span>
            </div>
            <div className={`pd2-timer ${timerDanger ? 'pd2-timer--danger' : ''}`}>
              <span className="pd2-timer-icon">⏱</span>
              {fmtTime(timeLeft)}
            </div>
          </div>

          {/* division + challenge */}
          <div className="pd2-challenge-strip">
            <div className="pd2-division">
              <span>{battle.divIcon}</span>
              <span>{battle.division} Division</span>
            </div>
            <p className="pd2-challenge-text">{battle.challenge}</p>
            <div className="pd2-situation">
              <span className="pd2-situation-label">CONTEXT</span>
              <span className="pd2-situation-val">{battle.situation}</span>
            </div>
          </div>

          {/* VS arena */}
          <div className="pd2-arena">

            {/* You */}
            <div className={`pd2-player pd2-player--you ${phase === 'done' && youWon ? 'pd2-player--winner' : ''} ${phase === 'done' && !youWon ? 'pd2-player--loser' : ''}`}>
              <div className="pd2-player-head">
                <div className="pd2-avatar pd2-avatar--you">Y</div>
                <div className="pd2-player-meta">
                  <span className="pd2-player-name">{battle.you.name}</span>
                  <span className="pd2-player-rank">{battle.you.rank}</span>
                </div>
                {phase === 'done' && youWon && <span className="pd2-crown">👑</span>}
              </div>
              <div className="pd2-prompt-box pd2-prompt-box--you">
                <pre className="pd2-prompt-pre">
                  {youPromptDisplay}
                  {phase === 'typing' && youTyped < battle.you.prompt.length && (
                    <span className="pd2-cursor" />
                  )}
                </pre>
              </div>
              {showScores && (
                <div className="pd2-score-reveal">
                  <ScoreRing score={battle.you.score} won={youWon} animate={showScores} />
                  <div className="pd2-breakdown">
                    <BreakdownBar label="Accuracy" val={battle.you.breakdown.accuracy} max={40} delay={400} />
                    <BreakdownBar label="Efficiency" val={battle.you.breakdown.efficiency} max={25} delay={600} />
                    <BreakdownBar label="Creativity" val={battle.you.breakdown.creativity} max={20} delay={800} />
                    <BreakdownBar label="Alignment" val={battle.you.breakdown.alignment} max={15} delay={1000} />
                  </div>
                </div>
              )}
            </div>

            {/* ⚔️ Swords VS Divider */}
            <SwordsDivider />

            {/* Opponent */}
            <div className={`pd2-player pd2-player--opp ${phase === 'done' && !youWon ? 'pd2-player--winner' : ''} ${phase === 'done' && youWon ? 'pd2-player--loser' : ''}`}>
              <div className="pd2-player-head">
                <div className="pd2-avatar pd2-avatar--opp">{battle.opponent.name[0].toUpperCase()}</div>
                <div className="pd2-player-meta">
                  <span className="pd2-player-name">{battle.opponent.name}</span>
                  <span className="pd2-player-rank">{battle.opponent.rank}</span>
                </div>
                {phase === 'done' && !youWon && <span className="pd2-crown">👑</span>}
              </div>
              <div className="pd2-prompt-box pd2-prompt-box--opp">
                <pre className="pd2-prompt-pre">
                  {oppPromptDisplay}
                  {phase === 'typing' && oppTyped < battle.opponent.prompt.length && (
                    <span className="pd2-cursor" />
                  )}
                </pre>
              </div>
              {showScores && (
                <div className="pd2-score-reveal">
                  <ScoreRing score={battle.opponent.score} won={!youWon} animate={showScores} />
                  <div className="pd2-breakdown">
                    <BreakdownBar label="Accuracy" val={battle.opponent.breakdown.accuracy} max={40} delay={500} />
                    <BreakdownBar label="Efficiency" val={battle.opponent.breakdown.efficiency} max={25} delay={700} />
                    <BreakdownBar label="Creativity" val={battle.opponent.breakdown.creativity} max={20} delay={900} />
                    <BreakdownBar label="Alignment" val={battle.opponent.breakdown.alignment} max={15} delay={1100} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* result banner — always mounted, space reserved up front, just toggled visible */}
          <div
            className={`pd2-result-banner ${youWon ? 'pd2-result-banner--win' : 'pd2-result-banner--loss'} ${phase === 'done' ? 'pd2-result-banner--show' : ''}`}
          >
            {youWon
              ? `🏆 You win! +18 ELO · Rank ${battle.you.rank} → climbing`
              : `💀 Opponent wins. −12 ELO · Study their breakdown`}
          </div>

          {/* card footer */}
          <div className="pd2-card-foot">
            <span className="pd2-foot-hint">AI judge scores in real-time · 40+ countries competing</span>
            <div className="pd2-format-pills">
              {['2 MIN', '5 MIN', '10 MIN'].map((f) => (
                <span key={f} className={`pd2-fp ${battle.format.startsWith(f.split(' ')[0]) ? 'pd2-fp--active' : ''}`}>{f}</span>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}